import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtDateTime as fmtDate, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";
import {
  PlusIcon, PencilSimpleIcon, ArrowUpIcon, ArrowDownIcon, TrashIcon, CheckCircleIcon, ProhibitIcon,
} from "@phosphor-icons/react";

/** Avisa a los formularios de empleados abiertos que el catálogo cambió. */
const avisar = () => window.dispatchEvent(new Event("hr-cargos-actualizados"));

/* ===================== CARGOS (catálogo único de puestos) ===================== */
export default function CargosTab() {
  const [data, setData] = useState({ items: [], puede_gestionar: false });
  const [loading, setLoading] = useState(true);
  const [verInactivos, setVerInactivos] = useState(true);
  const [editando, setEditando] = useState(null);   // {} = nuevo

  const load = async () => {
    try { const { data } = await api.get("/hr/cargos/gestion"); setData(data); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const puede = data.puede_gestionar;
  const items = data.items.filter((c) => verInactivos || c.activo !== false);

  const mover = async (c, delta) => {
    try { await api.put(`/hr/cargos/${c.id}`, { nombre: c.nombre, orden: c.orden + delta }); await load(); avisar(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const cambiarEstado = async (c) => {
    const activo = c.activo === false;
    if (!activo && !window.confirm(
      `¿Desactivar "${c.nombre}"? No podrá asignarse a nuevos empleados; ${c.empleados || 0} empleado(s) lo conservan.`)) return;
    try {
      await api.patch(`/hr/cargos/${c.id}/estado`, { activo });
      toast.success(activo ? "Cargo activado" : "Cargo desactivado");
      await load(); avisar();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const eliminar = async (c) => {
    if (!window.confirm(`¿Eliminar el cargo "${c.nombre}"? No tiene empleados ni historial.`)) return;
    try { await api.delete(`/hr/cargos/${c.id}`); toast.success("Cargo eliminado"); await load(); avisar(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div data-testid="hr-cargos">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-sm font-medium">Catálogo de cargos</div>
          <div className="text-[11px] text-text-muted">
            Mismo catálogo y orden que el formulario de empleados. Un cargo es el puesto laboral: no concede
            permisos del ERP (los roles Administrador y Delegado se asignan en Permisos).
          </div>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-xs text-text-muted flex items-center gap-1.5">
            <input type="checkbox" checked={verInactivos} onChange={(e) => setVerInactivos(e.target.checked)} data-testid="cargos-ver-inactivos" />
            Mostrar inactivos
          </label>
          {puede && (
            <button onClick={() => setEditando({})} data-testid="btn-add-cargo"
              className="bg-agro-green text-black px-3 py-2 rounded-md text-sm font-medium flex items-center gap-1.5 hover:opacity-90">
              <PlusIcon size={14} weight="bold" /> Añadir cargo
            </button>
          )}
        </div>
      </div>

      <div className="border border-border/60 rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="cargos-table">
          <thead className="bg-sidebar/60 text-[10px] uppercase tracking-wider text-text-muted">
            <tr>
              <th className="text-left px-3 py-2 w-16">Orden</th>
              <th className="text-left px-3 py-2">Cargo</th>
              <th className="text-right px-3 py-2">Empleados</th>
              <th className="text-left px-3 py-2">Estado</th>
              <th className="text-left px-3 py-2">Última modificación</th>
              {puede && <th className="text-right px-3 py-2">Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-3 py-8 text-center text-text-muted">Cargando…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-text-muted">Sin cargos</td></tr>}
            {items.map((c, i) => (
              <tr key={c.id} className={`border-t border-border/40 ${c.activo === false ? "opacity-60" : ""}`} data-testid={`cargo-row-${c.id}`}>
                <td className="px-3 py-2 font-mono text-xs text-text-muted">{c.orden}</td>
                <td className="px-3 py-2">
                  <div className="font-medium" data-testid="cargo-nombre">{c.nombre}</div>
                  {c.descripcion && <div className="text-[11px] text-text-muted">{c.descripcion}</div>}
                  <div className="flex gap-1.5 mt-0.5">
                    <span className="text-[10px] font-mono text-text-muted">{c.codigo}</span>
                    {c.es_rtc && <span className="text-[10px] px-1.5 rounded bg-agro-green/15 text-agro-green" title="Los empleados activos con este cargo aparecen como RTC en todo el ERP">Integración RTC</span>}
                  </div>
                </td>
                <td className="px-3 py-2 text-right text-xs">
                  {c.empleados_activos || 0} activos
                  {(c.empleados || 0) > (c.empleados_activos || 0) && <span className="text-text-muted"> · {c.empleados} total</span>}
                  {c.en_historial > 0 && <div className="text-[10px] text-text-muted">{c.en_historial} en historial</div>}
                </td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.activo === false ? "bg-red-500/10 text-red-400" : "bg-agro-green/10 text-agro-green"}`} data-testid="cargo-estado">
                    {c.activo === false ? "Inactivo" : "Activo"}
                  </span>
                </td>
                <td className="px-3 py-2 text-[11px] text-text-muted">
                  {c.updated_by || c.created_by || "—"}
                  {(c.updated_at || c.created_at) && <div>{fmtDate(c.updated_at || c.created_at)}</div>}
                </td>
                {puede && (
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      <IconBtn title="Subir" disabled={!verInactivos || i === 0} onClick={() => mover(c, -1)} icon={ArrowUpIcon} />
                      <IconBtn title="Bajar" disabled={!verInactivos || i === items.length - 1} onClick={() => mover(c, 1)} icon={ArrowDownIcon} />
                      <IconBtn title="Editar" onClick={() => setEditando(c)} icon={PencilSimpleIcon} testid={`cargo-edit-${c.id}`} />
                      <IconBtn title={c.activo === false ? "Activar" : "Desactivar"} onClick={() => cambiarEstado(c)}
                        icon={c.activo === false ? CheckCircleIcon : ProhibitIcon} testid={`cargo-toggle-${c.id}`} />
                      {c.eliminable && <IconBtn title="Eliminar (sin empleados ni historial)" onClick={() => eliminar(c)} icon={TrashIcon} testid={`cargo-del-${c.id}`} />}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editando && (
        <CargoModal cargo={editando} total={data.items.length} onClose={() => setEditando(null)}
          onSaved={async () => { setEditando(null); await load(); avisar(); }} />
      )}
    </div>
  );
}

function IconBtn({ title, onClick, icon: Icon, disabled, testid }) {
  return (
    <button type="button" title={title} aria-label={title} onClick={onClick} disabled={disabled} data-testid={testid}
      className="p-1.5 rounded border border-border/60 hover:bg-sidebar disabled:opacity-30 disabled:cursor-not-allowed">
      <Icon size={13} />
    </button>
  );
}

function CargoModal({ cargo, total, onClose, onSaved }) {
  const nuevo = !cargo.id;
  const [f, setF] = useState({
    nombre: cargo.nombre || "", descripcion: cargo.descripcion || "", orden: cargo.orden || total + 1,
  });
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (!f.nombre.trim()) return toast.error("El nombre del cargo es obligatorio");
    const body = { nombre: f.nombre, descripcion: f.descripcion, orden: Number(f.orden) || undefined };
    setSaving(true);
    try {
      if (nuevo) await api.post("/hr/cargos", body);
      else await api.put(`/hr/cargos/${cargo.id}`, body);
      toast.success(nuevo ? "Cargo añadido" : "Cargo actualizado");
      onSaved();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setSaving(false); }
  };
  const hist = [...(cargo.historial || [])].reverse().slice(0, 6);
  const m = useModalForm({ value: f, onClose, onSave: save });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <form onSubmit={m.guardar} data-testid="cargo-modal"
        className="bg-card border border-border rounded-xl w-full max-w-lg p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="font-medium">{nuevo ? "Añadir cargo" : `Editar cargo · ${cargo.codigo}`}</div>
          <BotonCerrar onClick={m.cerrar} />
        </div>
        <Campo l="Nombre del cargo *">
          <input value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} autoFocus data-testid="cargo-nombre-input"
            className="w-full bg-app border border-border rounded px-2 py-1.5 text-sm" maxLength={120} />
        </Campo>
        <Campo l="Descripción (opcional)">
          <textarea value={f.descripcion} onChange={(e) => setF({ ...f, descripcion: e.target.value })} rows={2} data-testid="cargo-desc-input"
            className="w-full bg-app border border-border rounded px-2 py-1.5 text-sm" />
        </Campo>
        <Campo l="Orden de visualización">
          <input type="number" min={1} max={Math.max(total + (nuevo ? 1 : 0), 1)} value={f.orden}
            onChange={(e) => setF({ ...f, orden: e.target.value })} data-testid="cargo-orden-input"
            className="w-28 bg-app border border-border rounded px-2 py-1.5 text-sm" />
          <div className="text-[10px] text-text-muted mt-1">Posición en el catálogo y en el selector de cargos de los empleados.</div>
        </Campo>
        {cargo.es_rtc && (
          <div className="text-[11px] text-agro-green bg-agro-green/10 rounded px-2 py-1.5">
            Cargo de integración RTC (código {cargo.codigo}). Puedes cambiar su nombre u orden; la integración se mantiene.
          </div>
        )}
        {hist.length > 0 && (
          <div className="border-t border-border/50 pt-2">
            <div className="text-[9px] uppercase tracking-wider text-text-muted mb-1">Historial de cambios</div>
            <ul className="space-y-0.5 text-[11px] text-text-muted" data-testid="cargo-historial">
              {hist.map((h, i) => (
                <li key={i}>{fmtDate(h.fecha)} · <span className="text-text-primary">{h.accion}</span> por {h.por}
                  {h.cambios && Object.keys(h.cambios).length > 0 && h.accion !== "creado" && (
                    <span> ({Object.keys(h.cambios).join(", ")})</span>)}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={m.cerrar} className="px-3 py-2 text-sm rounded-md border border-border">Cancelar</button>
          <button type="submit" disabled={saving || m.saving || !f.nombre.trim()} data-testid="cargo-guardar"
            className="px-3 py-2 text-sm rounded-md bg-agro-green text-black font-medium disabled:opacity-50">
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </form>
    </div>
  );
}

const Campo = ({ l, children }) => (
  <div>
    <label className="text-[9px] uppercase tracking-wider text-text-muted mb-1 block">{l}</label>
    {children}
  </div>
);
